import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowRight, CheckCircle2, XCircle, MoreHorizontal, RefreshCw,
  FileText, MessageSquare, History, Send, AlertCircle, AlertTriangle, Trash2, RotateCcw,
} from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  useRecordQuery,
  useApproveRecordMutation,
  useDeclineRecordMutation,
  useCheckFinalSubmitMutation,
  useFinalSubmitMutation,
  useTransitionToUnderReviewMutation,
  useDeclineCommentsQuery,
  useResolveCommentMutation,
  useSoftDeleteRecordMutation,
  useRestoreRecordMutation,
} from '../api/use-records-query'
import { useProjectConfigQuery } from '@/features/settings/api/project-config'
import { canDeleteRecord, canRestoreRecord } from '../lib/delete-permissions'
import { ConfirmDialog } from '@/components/shared/confirm-dialog'
import { useProjectRole } from '@/features/auth/context/project-role-context'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { useToast } from '@/hooks/use-toast'
import { ConflictError } from '../api/records-api'
import { RecordStatusBadge } from '../components/record-status-badge'
import { ApproveRecordDialog } from '@/features/review/components/approve-dialog'
import { DeclineRecordDialog } from '@/features/review/components/decline-dialog'
import { RecordFormsTab } from '../components/record-forms-tab'
import { RecordCommentsTab } from '../components/record-comments-tab'
import { RecordAuditTab } from '../components/record-audit-tab'
import type { FinalSubmitCheck } from '../api/records-api'
import { FinalSubmitDialog } from '../components/record-detail-panel'
import dayjs from 'dayjs'

// ─── Record Detail Page ───────────────────────────────────────────────────────

export function RecordDetailPage() {
  const { projectId, recordId } = useParams<{ projectId: string; recordId: string }>()
  const navigate = useNavigate()
  const { canApprove, canEdit, role } = useProjectRole()
  const user = useAuthStore((s) => s.user)

  const { data: record, isLoading, refetch } = useRecordQuery(recordId)
  const approveMutation = useApproveRecordMutation(projectId!)
  const declineMutation = useDeclineRecordMutation(projectId!)
  const checkFinalSubmitMutation = useCheckFinalSubmitMutation(projectId!)
  const finalSubmitMutation = useFinalSubmitMutation(projectId!)
  const underReviewMutation = useTransitionToUnderReviewMutation(projectId!)
  const { data: declineComments = [] } = useDeclineCommentsQuery(
    record?.status === 'needs_revision' ? recordId : undefined
  )
  const resolveCommentMutation = useResolveCommentMutation(recordId!)

  const [approveOpen, setApproveOpen] = useState(false)
  const [declineOpen, setDeclineOpen] = useState(false)
  const [finalSubmitOpen, setFinalSubmitOpen] = useState(false)
  const [finalSubmitResult, setFinalSubmitResult] = useState<FinalSubmitCheck | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const { data: projectConfig } = useProjectConfigQuery(projectId)
  const softDeleteMutation = useSoftDeleteRecordMutation(projectId!)
  const restoreMutation = useRestoreRecordMutation(projectId!)

  // Auto-transition submitted_for_review → under_review when approver opens
  useEffect(() => {
    if (
      canApprove &&
      record?.status === 'submitted_for_review' &&
      user &&
      projectId &&
      recordId
    ) {
      underReviewMutation.mutate({
        recordId: recordId,
        userId: user.id,
        userRole: role ?? 'approver',
      })
    }
    // Only fire once when record data arrives
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.status, canApprove])

  const canActOnRecord =
    canApprove &&
    record &&
    (record.status === 'submitted_for_review' || record.status === 'under_review')

  const canFinalSubmit =
    (canEdit || role === 'admin') &&
    record &&
    (record.status === 'draft' ||
      record.status === 'in_progress' ||
      record.status === 'needs_revision')

  const { toast } = useToast()

  const canDelete =
    record != null &&
    canDeleteRecord({ role, userId: user?.id, record, config: projectConfig })
  const canRestore = record != null && record.is_deleted && canRestoreRecord(role)

  const handleDeleteConfirm = async () => {
    if (!user || !record) return
    try {
      await softDeleteMutation.mutateAsync({ recordId: record.id, userId: user.id })
      setDeleteOpen(false)
      navigate(`/projects/${projectId}/records`)
    } catch (err) {
      setDeleteOpen(false)
      toast({
        title: 'خطا در حذف سابقه',
        description: (err as Error).message,
        variant: 'destructive',
      })
    }
  }

  const handleRestore = async () => {
    if (!user || !record) return
    try {
      await restoreMutation.mutateAsync({ recordId: record.id, userId: user.id })
      refetch()
    } catch (err) {
      toast({
        title: 'خطا در بازیابی سابقه',
        description: (err as Error).message,
        variant: 'destructive',
      })
    }
  }

  const handleFinalSubmitClick = async () => {
    if (!user || !record) return
    setFinalSubmitResult(null)
    setFinalSubmitOpen(true)
    try {
      const result = await checkFinalSubmitMutation.mutateAsync({ recordId: record.id })
      setFinalSubmitResult(result)
    } catch (err) {
      setFinalSubmitOpen(false)
      toast({
        title: 'خطا در بررسی پیش‌نیازهای ارسال نهایی',
        description: (err as Error).message,
        variant: 'destructive',
      })
    }
  }

  const handleFinalSubmitConfirm = async () => {
    if (!user || !record || !finalSubmitResult?.success) return
    try {
      await finalSubmitMutation.mutateAsync({
        recordId: record.id,
        userId: user.id,
        userRole: role ?? 'data_entry',
      })
      setFinalSubmitOpen(false)
      refetch()
    } catch (err) {
      const msg = err instanceof ConflictError
        ? 'تعارض نسخه: سابقه توسط کاربر دیگری تغییر کرده. صفحه را بارگذاری مجدد کنید.'
        : (err as Error).message
      toast({ title: 'خطا در ارسال نهایی', description: msg, variant: 'destructive' })
    }
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-8 bg-muted rounded animate-pulse w-48" />
        <div className="h-4 bg-muted rounded animate-pulse w-96" />
        <div className="grid grid-cols-4 gap-4 mt-8">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-10 bg-muted rounded animate-pulse" />
          ))}
        </div>
        <div className="h-64 bg-muted rounded animate-pulse" />
      </div>
    )
  }

  if (!record) {
    return (
      <div className="p-6 text-center text-muted-foreground text-sm">
        سابقه یافت نشد
      </div>
    )
  }

  const actions = (
    <div className="flex items-center gap-2">
      {canFinalSubmit && (
        <Button
          size="sm"
          className="gap-1.5"
          onClick={handleFinalSubmitClick}
          disabled={checkFinalSubmitMutation.isPending || finalSubmitMutation.isPending}
        >
          <Send className="w-4 h-4" />
          ارسال نهایی
        </Button>
      )}

      {canActOnRecord && (
        <>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10"
            onClick={() => setApproveOpen(true)}
          >
            <CheckCircle2 className="w-4 h-4" />
            تأیید
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 text-red-400 border-red-500/30 hover:bg-red-500/10"
            onClick={() => setDeclineOpen(true)}
          >
            <XCircle className="w-4 h-4" />
            رد
          </Button>
        </>
      )}
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => refetch()}>
        <RefreshCw className="w-4 h-4" />
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => navigate(`/projects/${projectId}/records`)}>
            بازگشت به لیست
          </DropdownMenuItem>
          {canDelete && (
            <DropdownMenuItem className="text-destructive" onClick={() => setDeleteOpen(true)}>
              <Trash2 className="w-4 h-4 me-2" />
              حذف سابقه
            </DropdownMenuItem>
          )}
          {canRestore && (
            <DropdownMenuItem onClick={handleRestore}>
              <RotateCcw className="w-4 h-4 me-2" />
              بازیابی سابقه
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border px-6 py-4 shrink-0">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
          <Link to={`/projects/${projectId}/records`} className="hover:text-foreground transition-colors">
            سوابق
          </Link>
          <ArrowRight className="w-3 h-3 rotate-180" />
          <span className="font-mono text-foreground">{record.display_id}</span>
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-bold text-foreground font-mono">{record.display_id}</h1>
            <RecordStatusBadge status={record.status} />
          </div>
          {actions}
        </div>

        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
          <span>ایجاد: {dayjs(record.created_at).format('YYYY/MM/DD')}</span>
          {record.submitted_at && (
            <span>ارسال: {dayjs(record.submitted_at).format('YYYY/MM/DD')}</span>
          )}
          {record.approved_at && (
            <span className="text-emerald-400">
              تأیید: {dayjs(record.approved_at).format('YYYY/MM/DD')}
            </span>
          )}
          {record.declined_at && (
            <span className="text-red-400">
              رد: {dayjs(record.declined_at).format('YYYY/MM/DD')}
            </span>
          )}
        </div>
      </div>

      {/* Status banners */}
      {record.status === 'needs_revision' && (
        <div className="bg-red-500/10 border-b border-red-500/20 px-6 py-3 space-y-2">
          <p className="text-sm text-red-400 font-medium">
            این سابقه نیاز به اصلاح دارد.
            {declineComments.length > 0 && (
              <span className="font-normal ms-1">
                {declineComments.length.toLocaleString('fa-IR')} نظر اصلاحی باز وجود دارد.
              </span>
            )}
          </p>
          {declineComments.length > 0 && (
            <div className="space-y-1.5">
              {declineComments.slice(0, 3).map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between gap-2 bg-red-500/5 border border-red-500/10 rounded-lg px-3 py-2"
                >
                  <div className="flex items-start gap-2 min-w-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0 mt-1.5" />
                    <p className="text-xs text-foreground truncate">
                      {c.field_key && (
                        <span className="text-red-400 font-medium me-1">[{c.field_key}]</span>
                      )}
                      {c.body}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs text-emerald-400 hover:text-emerald-300 shrink-0"
                    onClick={() =>
                      resolveCommentMutation.mutate({ commentId: c.id, userId: user!.id })
                    }
                    disabled={resolveCommentMutation.isPending}
                  >
                    حل شد
                  </Button>
                </div>
              ))}
              {declineComments.length > 3 && (
                <p className="text-xs text-muted-foreground pe-1">
                  و {(declineComments.length - 3).toLocaleString('fa-IR')} نظر دیگر — در تب «نظرات» مشاهده کنید
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {record.status === 'approved' && (
        <div className="bg-emerald-500/10 border-b border-emerald-500/20 px-6 py-3">
          <p className="text-sm text-emerald-400 font-medium">
            این سابقه تأیید شده است.
            {record.approval_comment && (
              <span className="font-normal ms-1 text-emerald-300">
                نظر تأییدکننده: {record.approval_comment}
              </span>
            )}
          </p>
        </div>
      )}

      {record.status === 'submitted_for_review' && (
        <div className="bg-blue-500/10 border-b border-blue-500/20 px-6 py-3">
          <p className="text-sm text-blue-400 font-medium">
            پرونده ارسال شده و در انتظار بررسی توسط تأییدکننده است.
          </p>
        </div>
      )}

      {record.status === 'under_review' && (
        <div className="bg-orange-500/10 border-b border-orange-500/20 px-6 py-3">
          <p className="text-sm text-orange-400 font-medium">
            پرونده در حال بررسی توسط تأییدکننده است.
          </p>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="forms" className="flex-1 flex flex-col overflow-hidden">
        <TabsList className="shrink-0 w-full justify-start rounded-none border-b border-border bg-transparent h-auto px-6 gap-0">
          {[
            { value: 'forms', label: 'فرم‌ها', icon: FileText },
            { value: 'comments', label: 'نظرات', icon: MessageSquare },
            { value: 'audit', label: 'تاریخچه', icon: History },
          ].map(({ value, label, icon: Icon }) => (
            <TabsTrigger
              key={value}
              value={value}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground px-4 py-3 gap-1.5"
            >
              <Icon className="w-4 h-4" />
              {label}
              {value === 'comments' && declineComments.length > 0 && (
                <span className="w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center">
                  {declineComments.length}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="flex-1 overflow-y-auto">
          <TabsContent value="forms" className="p-6 mt-0">
            <RecordFormsTab projectId={projectId!} recordId={recordId!} />
          </TabsContent>
          <TabsContent value="comments" className="p-6 mt-0">
            <RecordCommentsTab recordId={recordId!} />
          </TabsContent>
          <TabsContent value="audit" className="p-6 mt-0">
            <RecordAuditTab recordId={recordId!} projectId={projectId!} />
          </TabsContent>
        </div>
      </Tabs>

      {/* Dialogs */}
      {record && projectId && (
        <>
          <ApproveRecordDialog
            open={approveOpen}
            onClose={() => setApproveOpen(false)}
            onConfirm={async (comment) => {
              if (!user) return
              await approveMutation.mutateAsync({
                recordId: record.id,
                userId: user.id,
                userRole: role ?? 'approver',
                comment,
              })
              setApproveOpen(false)
            }}
            isLoading={approveMutation.isPending}
          />
          <DeclineRecordDialog
            open={declineOpen}
            onClose={() => setDeclineOpen(false)}
            onConfirm={async (comment) => {
              if (!user) return
              await declineMutation.mutateAsync({
                recordId: record.id,
                userId: user.id,
                userRole: role ?? 'approver',
                comment,
              })
              setDeclineOpen(false)
            }}
            isLoading={declineMutation.isPending}
            recordId={record.id}
          />
          <FinalSubmitDialog
            open={finalSubmitOpen}
            onClose={() => {
              setFinalSubmitOpen(false)
              setFinalSubmitResult(null)
            }}
            onConfirm={handleFinalSubmitConfirm}
            isLoading={checkFinalSubmitMutation.isPending || finalSubmitMutation.isPending}
            result={finalSubmitResult}
          />
        </>
      )}

      <ConfirmDialog
        open={deleteOpen}
        title="حذف سابقه"
        description={`سابقه ${record.display_id} حذف نرم می‌شود و از فهرست پیش‌فرض پنهان خواهد شد. مدیر پروژه می‌تواند آن را بازیابی کند.`}
        confirmLabel="حذف کن"
        pendingLabel="در حال حذف..."
        isPending={softDeleteMutation.isPending}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteOpen(false)}
      />
    </div>
  )
}
